/**
 * Admin API client.
 *
 * Every request is signed by the connected wallet (`slurp-admin:<unix-ms>`),
 * and the backend re-verifies the signature against the slurp-treasury
 * allowlist on EVERY call. There's no session token to leak.
 *
 * To avoid prompting Phantom on every render, we cache the most-recent
 * signed headers in module memory and reuse them until they're close to
 * the backend's 5-minute window. One sign every ~4 minutes, regardless
 * of how many endpoints the page hits.
 */

import bs58 from "bs58";

const API_BASE =
  process.env.REACT_APP_SLURP_API_URL || "http://localhost:4000";

// Backend rejects signatures older than 5 minutes. We refresh at 4 to leave
// headroom for clock skew and slow networks.
const SIG_REUSE_MS = 4 * 60 * 1000;

let cached = null;    // { address, ts, headers }
let inFlight = null;  // Promise<headers> — shared while a sign is pending

async function getSignedHeaders({ address, signMessage }) {
  if (!address) throw new Error("Wallet not connected");
  if (typeof signMessage !== "function") {
    throw new Error("Wallet does not support signMessage");
  }
  if (
    cached &&
    cached.address === address &&
    Date.now() - cached.ts < SIG_REUSE_MS
  ) {
    return cached.headers;
  }
  // De-dup concurrent callers — parallel fetches must not each prompt the
  // wallet. First caller kicks off the sign, everyone else awaits it.
  if (inFlight) return inFlight;
  inFlight = (async () => {
    try {
      const ts = Date.now();
      const msg = new TextEncoder().encode(`slurp-admin:${ts}`);
      const sig = await signMessage(msg);
      const headers = {
        "X-Admin-Address": address,
        "X-Admin-Ts": String(ts),
        "X-Admin-Sig": bs58.encode(sig),
      };
      cached = { address, ts, headers };
      return headers;
    } finally {
      inFlight = null;
    }
  })();
  return inFlight;
}

export function clearAdminAuthCache() {
  cached = null;
  inFlight = null;
}

async function get(path, ctx) {
  const headers = await getSignedHeaders(ctx);
  const r = await fetch(`${API_BASE}${path}`, { headers });
  if (r.status === 401) {
    // Backend rejected the cached signature — fall through, sign fresh,
    // and retry exactly once.
    cached = null;
    const fresh = await getSignedHeaders(ctx);
    const r2 = await fetch(`${API_BASE}${path}`, { headers: fresh });
    if (!r2.ok) throw new Error(`admin ${path} ${r2.status}`);
    return r2.json();
  }
  if (!r.ok) {
    const raw = await r.text();
    let detail;
    try { detail = JSON.parse(raw); } catch { detail = raw; }
    throw new Error(
      `admin ${path} ${r.status}: ${typeof detail === "string" ? detail : JSON.stringify(detail)}`
    );
  }
  return r.json();
}

async function del(path, ctx) {
  const headers = await getSignedHeaders(ctx);
  const r = await fetch(`${API_BASE}${path}`, { method: "DELETE", headers });
  if (r.status === 401) {
    cached = null;
    const fresh = await getSignedHeaders(ctx);
    const r2 = await fetch(`${API_BASE}${path}`, { method: "DELETE", headers: fresh });
    if (!r2.ok) throw new Error(`admin ${path} ${r2.status}`);
    return r2.json();
  }
  if (!r.ok) {
    const raw = await r.text();
    let detail;
    try { detail = JSON.parse(raw); } catch { detail = raw; }
    throw new Error(
      `admin ${path} ${r.status}: ${typeof detail === "string" ? detail : JSON.stringify(detail)}`
    );
  }
  return r.json();
}

async function post(path, body, ctx) {
  const headers = {
    ...(await getSignedHeaders(ctx)),
    "content-type": "application/json",
  };
  const r = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    headers,
    body: JSON.stringify(body ?? {}),
  });
  if (r.status === 401) {
    cached = null;
    const fresh = {
      ...(await getSignedHeaders(ctx)),
      "content-type": "application/json",
    };
    const r2 = await fetch(`${API_BASE}${path}`, {
      method: "POST",
      headers: fresh,
      body: JSON.stringify(body ?? {}),
    });
    if (!r2.ok) throw new Error(`admin ${path} ${r2.status}`);
    return r2.json();
  }
  if (!r.ok) {
    const raw = await r.text();
    let detail;
    try { detail = JSON.parse(raw); } catch { detail = raw; }
    throw new Error(
      `admin ${path} ${r.status}: ${typeof detail === "string" ? detail : JSON.stringify(detail)}`
    );
  }
  return r.json();
}

export const adminApi = {
  stats: (ctx) => get("/api/admin/stats", ctx),
  claimable: (ctx) => get("/api/admin/claimable", ctx),
  recentClaims: (ctx, limit = 20) => get(`/api/admin/recent-claims?limit=${limit}`, ctx),
  snapshots: (ctx) => get("/api/admin/leaderboard/snapshots", ctx),
  snapshot: (ctx, weekStart) =>
    get(`/api/admin/leaderboard/snapshots/${encodeURIComponent(weekStart)}`, ctx),
  markPaid: (ctx, id, tx, amountSol) =>
    post(`/api/admin/leaderboard/snapshots/${id}/mark-paid`, { tx, amount_sol: amountSol }, ctx),
  markBatchPaid: (ctx, week, { tx, payouts }) =>
    post(
      `/api/admin/leaderboard/snapshots/${encodeURIComponent(week)}/mark-batch-paid`,
      { tx, payouts },
      ctx
    ),
  buckets: (ctx) => get("/api/admin/buckets", ctx),
  bucketPayouts: (ctx, bucket, limit = 20) =>
    get(`/api/admin/buckets/payouts?bucket=${encodeURIComponent(bucket)}&limit=${limit}`, ctx),
  markBucketPaid: (ctx, { bucket, amountSol, tx, note }) =>
    post(`/api/admin/buckets/mark-paid`, { bucket, amount_sol: amountSol, tx, note }, ctx),
  jackpotEligible: (ctx, month) =>
    get(`/api/admin/jackpot/eligible?month=${encodeURIComponent(month)}`, ctx),
  jackpotUnpaidDrafts: (ctx) => get(`/api/admin/jackpot/unpaid`, ctx),
  jackpotDeleteWinner: (ctx, id) => del(`/api/admin/jackpot/winners/${id}`, ctx),
  leaderboardDeleteWeek: (ctx, week) =>
    del(`/api/admin/leaderboard/snapshots/week/${encodeURIComponent(week)}`, ctx),
  jackpotDraw: (ctx, month, drawnAt) =>
    post(`/api/admin/jackpot/draw`, { month, drawn_at: drawnAt }, ctx),
  jackpotMarkWinnerPaid: (ctx, { id, amountSol, tx }) =>
    post(`/api/admin/jackpot/mark-winner-paid`, { id, amount_sol: amountSol, tx }, ctx),
  broadcast: (ctx, { title, body, link }) =>
    post(`/api/admin/broadcast`, { title, body, link }, ctx),
  users: (ctx, { sort = "alltime", q = "", limit = 20, offset = 0 } = {}) =>
    get(
      `/api/admin/users?sort=${encodeURIComponent(sort)}&q=${encodeURIComponent(q)}&limit=${limit}&offset=${offset}`,
      ctx
    ),
};
